<template>
  <Disclosure
    v-if="$store.state.headerType === 'NORMAL' && !hideHeader"
    as="header"
    v-slot="{ open }"
    class="sable-header p-2 border-gray-50"
    :class="{ 'sticky top-0 z-50': isProfileRoute }"
    data-cy="sable-header"
  >
    <div :style="showStrapLine ? { backgroundColor: straplineBackgroundColor } : undefined" class="rounded-3xl">
      <div
        class="sable-navigation sable-header-navigation mx-auto flex xl:grid min-h-[4rem] items-center justify-between gap-6 bg-headerBg px-3 py-2 shadow-sm sm:min-h-0 sm:px-5 rounded-2xl border border-gray-100"
        :class="{ 'rounded-b-none': open }"
      >
        <div class="flex min-w-0 items-center gap-2">
          <DisclosureButton
            v-if="
              ((!$store.state.user && ($store.state.config.features.enableJoinFlow || $store.state.config.menu.loginPage)) ||
                natigationItems.length) &&
              !hideNavbarMenuItems
            "
            class="sable-icon-button-borderless-lg xl:hidden"
            data-cy="hamburger-button"
          >
            <span class="sr-only">{{ $t('header.openMainMenu') }}</span>
            <XIcon v-if="open" class="h-6 w-6" aria-hidden="true" />
            <MenuIcon v-else class="h-6 w-6" aria-hidden="true" />
          </DisclosureButton>

          <button
            class="flex shrink-0 items-center p-0.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            type="button"
            @click="gotoHomeUrl()"
          >
            <img class="h-7 max-w-[7.5rem] w-auto object-contain sm:h-8 sm:max-w-none" :src="logo" alt="Logo" data-cy="header-logo" />
          </button>
        </div>

        <!-- nowrap + scroll rather than wrap: nav items are client-configured, so no breakpoint can
             guarantee they fit. Below xl they live in the hamburger. Port the default header's
             greedy-nav + "…" menu if design wants them inline sooner. -->
        <nav
          v-if="!hideNavbarMenuItems"
          class="no-scrollbar hidden min-w-0 w-full flex-nowrap items-center gap-1 p-0.5 overflow-x-auto xl:col-start-2 xl:row-start-1 xl:flex"
          aria-label="Main navigation"
          data-cy="navigation-header"
        >
          <router-link v-for="item in natigationItems" :key="item.name" :to="item.route" :data-cy="item.cy" class="sable-nav-link">
            {{ $t(item.name) }}
          </router-link>
        </nav>

        <div class="ms-auto flex min-w-0 items-center gap-2 xl:col-start-3 xl:row-start-1">
          <template v-if="!$store.state.user && !hideNavbarMenuItems">
            <router-link
              v-if="$store.state.config.menu.loginPage"
              to="/login"
              data-cy="navbar-login-menu-link"
              class="sable-login-link hidden xl:inline-block"
            >
              {{ $t('header.login') }}
            </router-link>
            <router-link
              v-if="$store.state.config.features.enableJoinFlow"
              to="/join"
              data-cy="navbar-join-menu-link"
              class="sable-join-link hidden xl:inline-block"
            >
              {{ $t('header.register') }}
            </router-link>
          </template>

          <div class="sable-language relative z-50 flex items-center">
            <language-dropdown />
          </div>

          <Menu v-if="$store.state.user" as="div" class="relative" data-cy="profile-button">
            <MenuButton class="sable-profile-button" data-cy="header-profile-menu-button">
              <span class="sr-only">{{ $t('header.openUserMenu') }}</span>
              <UserIcon class="h-6 w-6" aria-hidden="true" />
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems class="sable-profile-menu" data-cy="profile-menu">
                <MenuItem v-if="$store.state.config.menu.profilePage" v-slot="{ active }">
                  <router-link to="/profile" data-cy="profile-link" class="sable-menu-item" :class="{ 'bg-primary-100': active }">
                    {{ $t('header.profile') }}
                  </router-link>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <button type="button" class="sable-menu-item w-full text-left" :class="{ 'bg-primary-100': active }" @click="signOut()">
                    {{ $t('header.logout') }}
                  </button>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>

      <DisclosurePanel
        static
        v-slot="{ open: mobileMenuOpen, close }"
        class="sable-mobile-menu relative z-50 mx-auto max-w-7xl xl:hidden"
        :aria-hidden="!mobileMenuOpen"
      >
        <nav
          class="sable-navigation sable-mobile-menu-panel bg-white p-3 shadow-sm rounded-b-2xl absolute top-0 w-full z-10 border border-t-0 border-gray-100"
          :class="{ 'sable-mobile-menu-panel-open': mobileMenuOpen }"
          aria-label="Main navigation"
          data-cy="sable-mobile-menu"
          :aria-hidden="!mobileMenuOpen"
          :inert="!mobileMenuOpen"
        >
          <router-link
            v-for="item in natigationItems"
            :key="item.name"
            :to="item.route"
            :data-cy="item.cy"
            class="sable-mobile-link"
            @click="close"
          >
            {{ $t(item.name) }}
          </router-link>
          <router-link
            v-if="!$store.state.user && $store.state.config.features.enableJoinFlow"
            to="/join"
            data-cy="hamburger-join-link"
            class="sable-mobile-link"
            @click="close"
          >
            {{ $t('header.register') }}
          </router-link>
          <router-link
            v-if="!$store.state.user && $store.state.config.menu.loginPage"
            to="/login"
            data-cy="hamburger-login-link"
            class="sable-mobile-link"
            @click="close"
          >
            {{ $t('header.login') }}
          </router-link>
        </nav>
      </DisclosurePanel>

      <div v-if="showStrapLine" class="sable-strapline overflow-hidden rounded-b-2xl">
        <the-strap-line></the-strap-line>
      </div>
    </div>
  </Disclosure>

  <!-- Keep the configured strapline visible when only the navigation bar is hidden. -->
  <div
    v-if="hideHeader && $store.state.headerType === 'NORMAL' && showStrapLine"
    :style="{ backgroundColor: straplineBackgroundColor }"
    class="sable-strapline m-2 overflow-hidden rounded-2xl"
  >
    <the-strap-line></the-strap-line>
  </div>

  <div v-if="$store.state.headerType === 'SMALL' && !hideHeader" class="sable-header bg-primary-500 px-3 py-3 sm:px-4 lg:px-8">
    <div class="sable-navigation mx-auto flex max-w-7xl items-center rounded-2xl bg-white px-5 py-2 shadow-sm">
      <img class="h-8 max-w-[7.5rem] w-auto object-contain sm:max-w-none" :src="logo" alt="Logo" data-cy="small-header-logo" />

      <div class="relative z-50 flex items-center ltr:ml-auto rtl:mr-auto">
        <language-dropdown />
      </div>
    </div>
  </div>

  <div v-if="$store.state.headerType === 'TINY' && !hideHeader" class="bg-primary-500" style="height: 0.3rem" data-cy="tiny-header"></div>

  <!-- SMALL/TINY headers still carry configured straplines; NORMAL renders it inside the header card above -->
  <div v-if="showStrapLine && $store.state.headerType !== 'NORMAL'" class="sable-strapline">
    <the-strap-line></the-strap-line>
  </div>

  <div v-if="showSableBannerHeader" class="relative w-full" data-cy="banner-header">
    <div class="absolute bottom-0 w-full bg-gradient-to-r from-primary-500 to-transparent rtl:bg-gradient-to-l">
      <div class="mx-auto max-w-7xl px-2 py-2 text-2xl font-bold text-opposite-500 sm:px-4 lg:px-6">{{ $t(title) }}</div>
    </div>
    <img class="h-72 w-full object-cover md:h-80 lg:h-96" :src="banner" alt="" />
  </div>

  <content-block v-if="$store.state.user && $store.state.headerType"></content-block>
</template>

<script lang="ts" src="./sable-header.component.ts" />

<style lang="less" scoped>
@tailwind components;
@layer components {
  .sable-nav-link {
    @apply whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 hover:text-primary-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500;
  }
  .sable-login-link {
    @apply rounded-full px-2 py-2 text-sm font-medium text-primary-700 whitespace-nowrap hover:bg-primary-50 hover:text-primary-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:px-3;
  }
  .sable-join-link {
    @apply rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-opposite-500 whitespace-nowrap hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2;
  }
  .sable-profile-button {
    @apply inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500;
  }
  .sable-profile-menu {
    @apply absolute ltr:right-0 rtl:left-0 z-50 mt-2 w-48 rounded-xl bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none;
  }
  // Compound selectors so the active rule outranks .sable-mobile-link/.sable-nav-link
  // regardless of source order (equal-specificity scoped rules otherwise let the later
  // link rule win and drop the active weight on the mobile menu).
  .sable-nav-link.router-link-active,
  .sable-mobile-link.router-link-active {
    @apply font-bold;
  }
  .sable-menu-item {
    @apply block px-4 py-2 text-sm text-primary-700 hover:text-primary-700 focus:outline-none;
  }
  .sable-mobile-link {
    @apply block rounded-xl px-3 py-2 text-base font-medium text-primary-700 hover:bg-primary-50 hover:text-primary-700  focus-visible:outline-primary-500;
  }
  .sable-strapline :deep([data-cy^='strapline-']) {
    @apply bg-primary-500 px-4 py-3 text-opposite-500;
    min-height: 2.75rem;
  }
  .sable-language :deep([data-cy='language-button']) {
    @apply text-primary-700 whitespace-nowrap hover:bg-primary-50 hover:text-primary-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 px-3 py-2;
  }

  .sable-header-navigation {
    transition: border-radius var(--sable-motion-fast) ease-in-out;
  }

  .sable-mobile-menu-panel {
    opacity: 0;
    transform: translateY(-0.5rem) scale(0.98);
    pointer-events: none;
    transition: opacity var(--sable-motion-fast) ease-in-out, transform var(--sable-motion-fast) ease-in-out;
  }

  .sable-mobile-menu-panel-open {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    .sable-mobile-menu-panel,
    .sable-header-navigation {
      transition: none;
    }
  }
}
</style>
